/* eslint-disable max-lines-per-function */
import type { FormEvent } from 'react';
import { useCallback, useMemo } from 'react';

import { useNavigate } from '@change-corgi/core/react/router';
import { useTracking } from '@change-corgi/core/react/tracking';

import { useHomepagePageUserData } from 'src/app/pages/homepage/pageContext';
import { isLoaded, isLoading } from 'src/app/shared/utils/async';

import { useCreateDraft } from './useCreateDraft';
import { useCreateRecommendations } from './useCreateRecommendations';

type SapPromptHookResult = ModelHookResult<
	{
		isSubmitting: boolean;
	},
	{
		onSubmit: (event: FormEvent<HTMLFormElement>) => void;
	}
>;

export function useSapIssueForm(): SapPromptHookResult {
	const track = useTracking();
	const navigate = useNavigate();
	const { data } = useHomepagePageUserData();

	const recommendationsExperiment = data.experiments.starfishSap3RecommendedAngles;

	const createDraftMutation = useCreateDraft();
	const createRecommendationsMutation = useCreateRecommendations();

	const isSubmitting = useMemo(() => {
		if (isLoading(createDraftMutation)) return true;
		if (isLoaded(createDraftMutation)) return true;
		if (isLoading(createRecommendationsMutation)) return true;
		if (isLoaded(createRecommendationsMutation)) return true;
		return false;
	}, [createDraftMutation, createRecommendationsMutation]);

	const handleSubmit = useCallback(
		(event: FormEvent<HTMLFormElement>) => {
			event.preventDefault();
			const formData = new FormData(event.target as HTMLFormElement);
			const input = (formData.get('issue') as string) || '';
			const trimmed = input.trim();
			void track('homepage_SAP_click', {
				cta: 'primary',
				prompt: trimmed,
			});

			if (isSubmitting) return;

			if (!trimmed.length) {
				navigate('/start-a-petition/intro?source_location=homepage_primary_button');
				return;
			}

			if (recommendationsExperiment.variation === 'variant_b') {
				createRecommendationsMutation.mutate(trimmed, {
					onSuccess: () => {
						navigate('/start-a-petition/issue?started_flow=false&source_location=homepage_primary_button');
					},
				});
				return;
			}

			createDraftMutation.mutate(trimmed);
		},
		[
			track,
			isSubmitting,
			recommendationsExperiment.variation,
			createDraftMutation,
			navigate,
			createRecommendationsMutation,
		],
	);

	return {
		data: {
			isSubmitting,
		},
		actions: {
			onSubmit: handleSubmit,
		},
	};
}
